import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { ApiError, getMeusNumeros } from "../../services/api";
import { Participacao } from "../../types";
import { Confirmation } from "./Confirmation";
import { EmptyState } from "./EmptyState";
import { ErrorState } from "./ErrorState";
import { LuckyNumbers } from "./LuckyNumbers";
import "./Results.css";

export function Results() {
  const navigate = useNavigate();
  const { token, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [participacoes, setParticipacoes] = useState<Participacao[]>([]);
  const [attempt, setAttempt] = useState(0);

  const retry = useCallback(() => {
    setError(null);
    setLoading(true);
    setAttempt((current) => current + 1);
  }, []);

  useEffect(() => {
    if (!token) return;

    let active = true;

    getMeusNumeros(token)
      .then((response) => {
        if (!active) return;
        setParticipacoes(response.data?.participacoes ?? []);
        setLoading(false);
      })
      .catch((err) => {
        if (!active) return;

        if (err instanceof ApiError && err.status === 401) {
          // Token expirado/inválido → volta ao login.
          logout();
          navigate("/login", { replace: true });
          return;
        }

        if (err instanceof ApiError && err.status === 429) {
          setError("Muitas tentativas. Aguarde um instante e tente novamente.");
        } else {
          setError("Não foi possível carregar seus números. Tente novamente.");
        }
        setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [token, attempt, logout, navigate]);

  // Números já atribuídos, sempre exibidos no formato oficial `numero_da_sorte`.
  const numbers = useMemo(
    () =>
      participacoes
        .filter((participacao) => participacao.status === "atribuida")
        .flatMap((participacao) => participacao.numeros ?? [])
        .map((numero) => numero.numero_da_sorte)
        .filter(Boolean),
    [participacoes]
  );

  const emRevisao = useMemo(
    () => participacoes.some((participacao) => participacao.status === "em_revisao"),
    [participacoes]
  );

  const liberacaoPrevista = useMemo(() => {
    const datas = participacoes
      .filter((participacao) => participacao.status === "elegivel")
      .map((participacao) => participacao.liberacao_prevista)
      .filter((data): data is string => Boolean(data))
      .sort();
    return datas[0] ?? null;
  }, [participacoes]);

  const temParticipacaoPendente = useMemo(
    () =>
      participacoes.some(
        (participacao) =>
          participacao.status === "elegivel" || participacao.status === "em_revisao"
      ),
    [participacoes]
  );

  if (loading) {
    return (
      <div
        className="results results--loading"
        role="status"
        aria-label="Carregando"
      >
        <div className="results__spinner" />
        <p className="results__loading-text">Carregando seus dados...</p>
      </div>
    );
  }

  if (error) {
    return <ErrorState message={error} onRetry={retry} />;
  }

  if (numbers.length > 0) {
    return <LuckyNumbers numbers={numbers} />;
  }

  if (temParticipacaoPendente) {
    return (
      <Confirmation emRevisao={emRevisao} liberacaoPrevista={liberacaoPrevista} />
    );
  }

  return <EmptyState />;
}
